/**
 * ROUTING ARCHITECTURE RULES
 * ==========================
 * 
 * This file (App.jsx) is the ONLY place where <Routes> should exist in the application.
 * 
 * ROUTING PRINCIPLES:
 * 1. Single <Routes> tree at the application root
 * 2. All nested Route paths must be relative (no leading "/")
 * 3. Absolute paths only allowed in top-level routes (public routes outside layouts)
 * 4. Navigation (navigate(), <Link to>) can use absolute paths (they're URLs, not routes)
 * 5. Layout routes (AppChrome, AgentShell, AdminShell) use <Outlet /> to render child routes
 * 
 * ARCHITECTURE:
 * - AppChrome: Global layout (Topbar + global UI, persistent across all authenticated routes)
 * - AgentShell: Agent-facing layout (Sidebar + content area)
 * - AdminShell: Admin-facing layout (Admin Sidebar + content area, superset of Agent)
 * 
 * ROUTE HIERARCHY:
 * AppChrome (layout) → Shell (layout) → Page Component
 */

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import React, { useContext, useEffect, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { UserProvider, UserContext } from "./context/UserContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AgentNotificationProvider } from "./context/AgentNotificationContext";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import Dashboard from "./pages/agent/dashboard/Dashboard";
import ClientManagement from "./pages/agent/clients/ClientManagement";
import DisneyWorld from "./pages/agent/destinations/DisneyWorld";
import Disneyland from "./pages/agent/destinations/Disneyland";
import UniversalOrlando from "./pages/agent/destinations/UniversalOrlando";
import DisneyCruise from "./pages/agent/destinations/DisneyCruise";
import Login from "./pages/agent/auth/Login";
import ForgotPassword from "./pages/agent/auth/ForgotPassword";
import ResetPassword from "./pages/agent/auth/ResetPassword";
import LoginRouteGuard from "./components/auth/LoginRouteGuard";
import RoleBasedRedirect from "./components/auth/RoleBasedRedirect";
import StandaloneAuthGate from "./components/auth/StandaloneAuthGate";
import Settings from "./pages/agent/settings/Settings";
import Subscription from "./pages/agent/settings/Subscription";
import SubscriptionCheckout from "./pages/agent/settings/SubscriptionCheckout";
// import { Toaster } from "react-hot-toast";
import { EnhancedInactivityProvider } from "./context/EnhancedInactivityContext";
// import ModernSignup from "./pages/agent/ModernSignup"; // Disabled - signup via ParkProHome only
import Notifications from "./pages/agent/notifications/Notifications";
import ItineraryView from "./pages/agent/trips/itinerary/ItineraryView";
import ItineraryViewFixture from "./pages/agent/trips/itinerary/ItineraryViewFixture";
import Questionnaire from "./pages/agent/questionnaire/Questionnaire";
import SalesPipeline from "./pages/agent/sales/SalesPipeline";
import LeadCaptureWidget from "./pages/widget/LeadCaptureWidget";
import LeadCapturePage from "./pages/public/LeadCapturePage";
import PixieDustAnimationPage from "./pages/shared/PixieDustAnimationPage";
import { ToastProvider } from "./context/ToastContext";
import ToastDemo from "./components/toast/ToastDemo";
import advancedAnalytics from "./services/advancedAnalytics";
import AppChrome from "./components/layout/AppChrome";
import AgentShell from "./components/layout/AgentShell";
import AdminShell from "./components/layout/AdminShell";
import { canAccessAdmin } from "./utils/roleUtils";
import PhaseGuard from "./components/shared/PhaseGuard";

// CRM Components
import CRMDashboard from "./components/agent/crm/CRMDashboard";
import CRMTrips from "./pages/agent/crm/Trips";
import CRMLeads from "./pages/agent/crm/Leads";
import CRMDisneyWorld from "./pages/agent/crm/destinations/DisneyWorld";
import CRMDisneyland from "./pages/agent/crm/destinations/Disneyland";
import CRMUniversalOrlando from "./pages/agent/crm/destinations/UniversalOrlando";
import CRMDisneyCruise from "./pages/agent/crm/destinations/DisneyCruise";
import Commissions from "./pages/agent/crm/Commissions";
import CRMPipelineManagement from "./components/agent/crm/CRMPipelineManagement";
import Communications from "./pages/agent/crm/Communications";
import CRMSubscriptionManagement from "./components/agent/crm/CRMSubscriptionManagement";
import CRMPlaceholder from "./components/agent/crm/CRMPlaceholder";

// Admin Components
import AgencyDashboard from "./pages/agency-admin/AgencyDashboard";
import AgentManagement from "./pages/agency-admin/AgentManagement";
import AgentDetail from "./pages/agency-admin/AgentDetail";
import FinancialManagement from "./pages/agency-admin/FinancialManagement";
import WhiteLabeling from "./pages/agency-admin/WhiteLabeling";
import Reports from "./pages/agency-admin/Reports";
import ActivityLog from "./pages/agency-admin/ActivityLog";
import AttentionInbox from "./pages/agency-admin/AttentionInbox";
import AgentDocumentsPage from "./pages/agent/documents/AgentDocumentsPage";
import AdminDocumentsPage from "./pages/agency-admin/AdminDocumentsPage";
import AdminClientsPage from "./pages/agency-admin/AdminClientsPage";

// Experimental questionnaire playground (dev only)
const QuestionnairePlayground = React.lazy(() =>
  import("./experiments/questionnaire/QuestionnairePlayground")
);

// Protected Route wrapper
const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requireSpecialAccess = false,
}) => {
  const context = useContext(UserContext) || {};
  const { user, loadingUser } = context;
  const isAuthenticated = !!user;

  useEffect(() => {
    if (user) {
      advancedAnalytics.setUserContext(user);
    }
  }, [user]);

  if (loadingUser) {
    return (
      <div className="flex justify-center items-center min-h-screen text-xl text-white">
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/agent/login" replace />;
  }

  if (requireAdmin && !canAccessAdmin(user?.role)) {
    return <Navigate to="/agent/dashboard" replace />;
  }

  if (requireSpecialAccess && !canAccessAdmin(user?.role)) {
    return <Navigate to="/agent/dashboard" replace />;
  }

  return children;
};

// Main App Routes - Wrapped in AppChrome for persistent Topbar
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes - outside AppChrome (no Topbar) */}
      <Route path="/questionnaire/:tripId" element={<Questionnaire />} />
      <Route path="/test-questionnaire" element={<Questionnaire />} />
      <Route path="/test-toast" element={<ToastDemo />} />
      {process.env.NODE_ENV !== "production" && (
        <Route
          path="/dev/questionnaire"
          element={
            <Suspense
              fallback={
                <div className="flex justify-center items-center min-h-screen text-xl text-white">
                  Loading Playground...
                </div>
              }
            >
              <QuestionnairePlayground />
            </Suspense>
          }
        />
      )}
      <Route 
        path="/agent/login" 
        element={
          <UserProvider>
            <LoginRouteGuard>
              <Login />
            </LoginRouteGuard>
          </UserProvider>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <UserProvider>
            <ForgotPassword />
          </UserProvider>
        } 
      />
      <Route 
        path="/reset-password" 
        element={<ResetPassword />}
      />
      <Route path="/widget/:agencyId" element={<LeadCaptureWidget />} />
      <Route path="/lead/:agentKey" element={<LeadCapturePage />} />

      {/* Authenticated routes - AppChrome as layout route (persistent Topbar) */}
      <Route
        path="/*"
        element={
          <EnhancedInactivityProvider userRole="agent">
            <UserProvider>
              <AgentNotificationProvider>
                <AppChrome />
              </AgentNotificationProvider>
            </UserProvider>
          </EnhancedInactivityProvider>
        }
      >
        {/* Standalone routes - NO shell (no Sidebar/Topbar chrome from shells) */}
        {/* These routes are full-screen experiences and should not show shell UI */}
        <Route
          path="agent/pixie-dust-animation"
          element={
            <StandaloneAuthGate>
              <PixieDustAnimationPage />
            </StandaloneAuthGate>
          }
        />
        <Route
          path="agent/itinerary/view/:id"
          element={
            <StandaloneAuthGate>
              <ItineraryView />
            </StandaloneAuthGate>
          }
        />
        {import.meta.env.DEV && (
          <Route
            path="agent/itinerary/fixture"
            element={
              <StandaloneAuthGate>
                <ItineraryViewFixture />
              </StandaloneAuthGate>
            }
          />
        )}
        <Route
          path="agent/settings/subscription"
          element={
            <StandaloneAuthGate>
              <Subscription />
            </StandaloneAuthGate>
          }
        />
        <Route
          path="agent/settings/subscription/checkout"
          element={
            <StandaloneAuthGate>
              <SubscriptionCheckout />
            </StandaloneAuthGate>
          }
        />

        {/* Agent routes - nested under AgentShell */}
        <Route
          path="agent/*"
          element={
            <ProtectedRoute>
              <AgentShell />
            </ProtectedRoute>
          }
        >
          {/* Role-based redirect for /agent entry point */}
          <Route index element={<RoleBasedRedirect />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="trips" element={<Navigate to="crm/trips" replace />} />
          <Route path="disney-world" element={<DisneyWorld />} />
          {/* Phase 1 destination routes - wrapped with PhaseGuard */}
          <Route
            path="disneyland"
            element={
              <ProtectedRoute requireSpecialAccess={true}>
                <PhaseGuard><Disneyland /></PhaseGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="universal-orlando"
            element={
              <ProtectedRoute requireSpecialAccess={true}>
                <PhaseGuard><UniversalOrlando /></PhaseGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="disney-cruise"
            element={
              <ProtectedRoute requireSpecialAccess={true}>
                <PhaseGuard><DisneyCruise /></PhaseGuard>
              </ProtectedRoute>
            }
          />
          <Route path="crm/clients" element={<ClientManagement />} />
          <Route path="crm/clients/new" element={<ClientManagement />} />
          <Route path="crm/clients/:id" element={<ClientManagement />} />
          
          {/* CRM Routes - flattened from CRMRoutes.jsx */}
          {/* Note: "crm" route matches /agent/crm exactly (was index route in CRMRoutes) */}
          {/* Phase 1 routes - wrapped with PhaseGuard for soft blocking */}
          <Route path="crm" element={<PhaseGuard><CRMDashboard /></PhaseGuard>} />
          <Route path="crm/trips" element={<PhaseGuard><CRMTrips /></PhaseGuard>} />
          <Route path="crm/trips/:id" element={<PhaseGuard><CRMPlaceholder title="Trip Details" /></PhaseGuard>} />
          <Route path="crm/leads" element={<PhaseGuard><CRMLeads /></PhaseGuard>} />
          <Route path="crm/leads/:id" element={<PhaseGuard><CRMPlaceholder title="Lead Details" /></PhaseGuard>} />
          <Route path="crm/disney-world" element={<PhaseGuard><CRMDisneyWorld /></PhaseGuard>} />
          <Route path="crm/disneyland" element={<PhaseGuard><CRMDisneyland /></PhaseGuard>} />
          <Route path="crm/universal-orlando" element={<PhaseGuard><CRMUniversalOrlando /></PhaseGuard>} />
          <Route path="crm/disney-cruise" element={<PhaseGuard><CRMDisneyCruise /></PhaseGuard>} />
          <Route path="crm/commissions" element={<PhaseGuard><Commissions /></PhaseGuard>} />
          <Route path="crm/commissions/:id" element={<PhaseGuard><CRMPlaceholder title="Commission Details" /></PhaseGuard>} />
          <Route path="crm/pipeline" element={<PhaseGuard><CRMPipelineManagement /></PhaseGuard>} />
          <Route path="crm/communications" element={<PhaseGuard><Communications /></PhaseGuard>} />
          {/* Phase 2 routes */}
          <Route path="crm/communications/campaigns" element={<PhaseGuard><CRMPlaceholder title="Email Campaigns" /></PhaseGuard>} />
          <Route path="crm/analytics" element={<PhaseGuard><CRMPlaceholder title="Analytics Dashboard" /></PhaseGuard>} />
          <Route path="crm/analytics/reports" element={<PhaseGuard><CRMPlaceholder title="Reports" /></PhaseGuard>} />
          <Route path="crm/subscriptions" element={<PhaseGuard><CRMSubscriptionManagement /></PhaseGuard>} />
          <Route path="crm/automation" element={<PhaseGuard><CRMPlaceholder title="Workflow Automation" /></PhaseGuard>} />
          <Route path="crm/automation/followup" element={<PhaseGuard><CRMPlaceholder title="Follow-up Automation" /></PhaseGuard>} />
          <Route path="crm/settings" element={<PhaseGuard><CRMPlaceholder title="CRM Settings" /></PhaseGuard>} />
          
          <Route path="notifications" element={<Notifications />} />
          <Route path="settings" element={<Settings />} />
          <Route path="documents" element={<AgentDocumentsPage />} />
          {/* Phase 1 sales pipeline - wrapped with PhaseGuard */}
          <Route
            path="sales-pipeline"
            element={
              <ProtectedRoute requireSpecialAccess={true}>
                <PhaseGuard><SalesPipeline /></PhaseGuard>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Admin routes - nested under AdminShell - flattened from AgencyAdminRoutes.jsx */}
        {/* Phase 2 routes - wrapped with PhaseGuard for soft blocking */}
        <Route
          path="agent/admin/*"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<PhaseGuard><AgencyDashboard /></PhaseGuard>} />
          <Route path="agents" element={<PhaseGuard><AgentManagement /></PhaseGuard>} />
          <Route path="agents/:id" element={<PhaseGuard><AgentDetail /></PhaseGuard>} />
          <Route path="financial" element={<PhaseGuard><FinancialManagement /></PhaseGuard>} />
          <Route path="white-labeling" element={<PhaseGuard><WhiteLabeling /></PhaseGuard>} />
          <Route path="reports" element={<PhaseGuard><Reports /></PhaseGuard>} />
          <Route path="activity-log" element={<PhaseGuard><ActivityLog /></PhaseGuard>} />
          <Route path="attention" element={<PhaseGuard><AttentionInbox /></PhaseGuard>} />
          <Route path="documents" element={<PhaseGuard><AdminDocumentsPage /></PhaseGuard>} />
          <Route path="clients" element={<PhaseGuard><AdminClientsPage /></PhaseGuard>} />
        </Route>

        {/* Fallback - redirect to role-based landing */}
        <Route path="*" element={<RoleBasedRedirect />} />
      </Route>

      <Route
        path="*"
        element={
          <div className="p-10 text-center text-white">
            404 – Page Not Found
          </div>
        }
      />
    </Routes>
  );
};

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  if (
    window &&
    window.location &&
    window.location.search.includes("debugroot")
  ) {
    return (
      <div
        style={{
          padding: 40,
          fontSize: 20,
          color: "green",
          background: "#fff",
        }}
      >
        App() Rendered!
      </div>
    );
  }
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <ThemeProvider>
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </ThemeProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;